import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, switchMap, tap } from 'rxjs';

import moment from 'moment';
import { SessionStorageService } from 'ngx-webstorage';
import { TreeNode } from 'primeng/api';

import { User } from 'src/models/users';
import { FilterCache } from 'src/models/filterCache';
import { environment } from 'src/environments/environment';
import { TargetEnvironment } from 'src/environments/environment.interfaces';
import { TestResultsTableService } from 'src/modules/start-page/services/test-results-table-service';
import { NexusService } from 'src/services/nexus.service';
import { FunctionalGroup } from 'src/models/functional-group';
import { Group } from 'src/models/group';
import { AuthService } from 'src/services/auth.service';

import { ExternalModeService } from './../../../../services/external-mode.service';
import { VehicleName } from './../../../../models/vehicleName';

// used to "model" the dropdown of organizations.
// Display value is what will be displayed and value is the actual value.
export interface DropDownType {
	display: string;
	value: string;
}
@Component({
	selector: 'app-filter',
	templateUrl: './filter.component.html',
	styleUrls: ['./filter.component.scss'],
})
export class FilterComponent implements OnInit, OnDestroy {
	environment = environment.environment;

	TargetEnvironment = TargetEnvironment;

	filterSettings: FilterCache;

	allUsersInfo: User[];

	vehicles: Array<VehicleName> = [{ id: 0, name: 'All' }];

	orgsList: DropDownType[] = [{ display: 'All', value: 'All' }];

	selectedInitialOrgs: DropDownType[];

	groupsList: DropDownType[] = [{ display: 'All', value: 'All' }];

	ecuSWIdentifiers: TreeNode[] = [];

	selectedEcuSWIdentifiers: TreeNode[] = [];

	userGroup = '';

	showOrganizationFilter: boolean;

	public functionalGroups$ = this.nexusService.allFunctionalGroups$;

	public groups$ = this.nexusService.allGroups$;

	public filtersReady = new BehaviorSubject<boolean>(false);

	constructor(
		private sessionStorage: SessionStorageService,
		private testResultsTableService: TestResultsTableService,
		private router: Router,
		private nexusService: NexusService,
		private authService: AuthService,
		private externalModeService: ExternalModeService,
	) {}

	ngOnDestroy(): void {
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
	}

	ngOnInit(): void {
		// Session storage: store until user closes tab/web browser
		// if we have a filter already in our session storage, take it instead
		if (this.sessionStorage.retrieve('filterSettings')) {
			const filter = this.sessionStorage.retrieve('filterSettings');
			this.filterSettings = { ...filter };
			this.testResultsTableService.SetTable(this.filterSettings);
			this.filtersReady.next(true);
		} else {
			// If we have nothing in our session storage then create new filter settings object.
			// Default filter settings for users without linked organization.

			combineLatest({
				userData: this.authService.userData$.pipe(switchMap(user => this.nexusService.getNexusUser$(user.userName))),
				functionalGroups: this.nexusService.allFunctionalGroups$,
				isExternal: this.externalModeService.isExternalMode$,
			})
				.pipe(
					tap(source => {
						this.selectedInitialOrgs = [{ display: 'All', value: 'All' }];
						const startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
						const endDate = moment().endOf('day').toISOString();

						const userFunctionalGroup = source.isExternal
							? null
							: source.functionalGroups.find(group => group.uid === source.userData?.functionalGroups.find(userGroup => userGroup.isDefault)?.functionalGroupUid);
						this.filterSettings = {
							startDate,
							endDate,
							usersInOrg: [],
							vehicle: this.vehicles[0],
							selectedOrgs: [this.orgsList[0]],
							ecuSwIdentifiers: [],
							functionalGroup: userFunctionalGroup,
							group: null,
						};
						this.testResultsTableService.SetTable(this.filterSettings);
						this.filtersReady.next(true);
					}),
				)
				.subscribe();
		}
	}

	// used for multiple select to set initial value
	private comparer(o1: DropDownType, o2: DropDownType): boolean {
		// if possible compare by object's value property - and not by reference.
		return o1 && o2 ? o1.value === o2.value : o2 === o2;
	}

	private filterFilterSettings(group: DropDownType, org: DropDownType[]) {
		let filteredUsers = this.allUsersInfo;
		filteredUsers = group.value === 'All' ? filteredUsers : filteredUsers.filter(s => s.groups.find(g => g === group.value));
		filteredUsers = org.length === 0 || org[0].value === 'All' ? filteredUsers : filteredUsers.filter(x => org.map(o => o.value).includes(x.organization));
		this.filterSettings = { ...this.filterSettings, usersInOrg: filteredUsers };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
	}

	private UpdateSelectedVehicle(vid: string): void {
		this.RemoveQueryString();
		const vechileId = parseInt(vid, 10);
		// if our vehicle id is 0 (comes if you select the All option), take a dummy (empty) vehicle
		const selectedVeh = vid === '0' ? { name: 'All', id: 0 } : this.vehicles.find(v => v.id === vechileId);
		this.filterSettings = { ...this.filterSettings, vehicle: selectedVeh };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.ApplyFilters(this.filterSettings);
	}

	public OnStartDateChange($event) {
		this.RemoveQueryString();
		this.filterSettings = { ...this.filterSettings, startDate: $event.format('YYYY-MM-DD') };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.isLoadingTable.next(true);
		this.testResultsTableService.SetTable(this.filterSettings);
	}

	public OnEndDateChange($event: moment.Moment) {
		this.RemoveQueryString();
		const endDate = $event.endOf('day').toISOString();
		this.filterSettings = { ...this.filterSettings, endDate };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.isLoadingTable.next(true);
		this.testResultsTableService.SetTable(this.filterSettings);
	}

	public onFunctionalGroupChange(functionalGroup: FunctionalGroup) {
		this.RemoveQueryString();
		this.filterSettings = { ...this.filterSettings, functionalGroup };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.isLoadingTable.next(true);
		this.testResultsTableService.SetTable(this.filterSettings);
	}

	public onGroupChange(group: Group) {
		this.RemoveQueryString();
		this.filterSettings = { ...this.filterSettings, group };
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.isLoadingTable.next(true);
		this.testResultsTableService.SetTable(this.filterSettings);
	}

	private applyEcuFilter() {
		this.RemoveQueryString();
		this.filterSettings = { ...this.filterSettings, ecuSwIdentifiers: this.selectedEcuSWIdentifiers };
		// setting the parent values to undefined because of error in Converting circular structure to JSON
		this.filterSettings.ecuSwIdentifiers.forEach(element => {
			if (element.parent) {
				element.parent = undefined;
			}
			// if(element.children){
			//   element.children.forEach(element => {
			//     // element.parent = undefined;
			//   });
			// }
		});
		this.sessionStorage.store('filterSettings', this.filterSettings ?? '');
		this.testResultsTableService.ApplyFilters(this.filterSettings);
	}

	//Remove querystring for property if filters are being used after search with querystring,
	//otherwise the filters won't work
	private RemoveQueryString(): void {
		if (this.router.url.includes('testrun/property')) {
			const url: string = this.router.url.substring(0, this.router.url.indexOf('testrun/property?'));
			this.router.navigateByUrl(url);
		}
	}
}
